import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CellulariEditor } from "./DatiSocietariShared";

export default function ClientNew() {
    const navigate = useNavigate();
    const [companyName, setCompanyName] = useState("");
    const [vat, setVat] = useState("");
    const [address, setAddress] = useState("");
    const [pec, setPec] = useState("");
    const [assignedId, setAssignedId] = useState("");
    const [internalUsers, setInternalUsers] = useState([]);
    const [cuName, setCuName] = useState("");
    const [cuEmail, setCuEmail] = useState("");
    // Phase 9 Dati societari
    const [capitaleSociale, setCapitaleSociale] = useState("");
    const [numeroRea, setNumeroRea] = useState("");
    const [amministratore, setAmministratore] = useState("");
    const [sedeLegale, setSedeLegale] = useState("");
    const [sedeOperativa, setSedeOperativa] = useState("");
    const [cellulari, setCellulari] = useState([""]);
    const [sitoWeb, setSitoWeb] = useState("");

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/internal/internal-users", { params: { active: true } });
                setInternalUsers(data.items || []);
                if (data.items?.length === 1) setAssignedId(data.items[0].id);
            } catch {
                toast.error("Impossibile caricare i referenti interni");
            }
        })();
    }, []);

    const onSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (loading) return;
        setError(""); setLoading(true);
        try {
            const filteredCel = cellulari.map(s => s.trim()).filter(Boolean);
            const payload = {
                company_name: companyName.trim(),
                vat_or_taxcode: vat.trim(),
                address: address.trim() || null,
                pec_email: pec.trim(),
                assigned_internal_user_id: assignedId,
                initial_client_user: { name: cuName.trim(), email: cuEmail.trim() },
                // Phase 9 Dati societari (tutti opzionali)
                capitale_sociale: capitaleSociale.trim() || null,
                numero_rea: numeroRea.trim() || null,
                amministratore: amministratore.trim() || null,
                sede_legale: sedeLegale.trim() || null,
                sede_operativa: sedeOperativa.trim() || null,
                cellulari: filteredCel,
                sito_web: sitoWeb.trim() || null,
            };
            const { data } = await api.post("/internal/clients", payload);
            toast.success(`Società creata. Invito inviato a ${cuEmail.trim()}.`);
            navigate(`/app/clienti/${data.id}`, { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail;
            setError(typeof msg === "string" ? msg : "Errore durante la creazione");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: "32px 40px", maxWidth: "780px" }} data-testid="clients-new-page">
            <Link to="/app/clienti" className="lt-link" style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "20px" }} data-testid="clients-new-back-link">
                <ArrowLeft size={14} /> Torna alle società clienti
            </Link>

            <h1 className="lt-h1" data-testid="clients-new-heading" style={{ marginBottom: "8px" }}>Nuova società</h1>
            <p className="lt-sub" style={{ marginBottom: "24px" }}>Inserisci i dati della società e del primo utente.</p>

            <form onSubmit={onSubmit} method="post" action="#" noValidate className="lt-card" style={{ padding: "32px" }} data-testid="clients-new-form">
                <h2 className="lt-section-title">Dati società</h2>

                <label htmlFor="cn" className="lt-label">Ragione sociale</label>
                <input id="cn" className="lt-field" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required
                       data-testid="client-new-company-name" placeholder="Acme S.p.A." />

                <label htmlFor="vat" className="lt-label">P.IVA o Codice Fiscale</label>
                <input id="vat" className="lt-field" value={vat} onChange={(e) => setVat(e.target.value)} required
                       data-testid="client-new-vat" placeholder="IT01234567890" />

                <label htmlFor="addr" className="lt-label">Indirizzo</label>
                <input id="addr" className="lt-field" value={address} onChange={(e) => setAddress(e.target.value)}
                       data-testid="client-new-address" placeholder="Via dell'Energia 12, 20100 Milano" />

                <label htmlFor="pec" className="lt-label">PEC</label>
                <input id="pec" className="lt-field" type="email" value={pec} onChange={(e) => setPec(e.target.value)} required
                       data-testid="client-new-pec" placeholder="acme@pec.it" />

                <label htmlFor="ref" className="lt-label">Referente interno</label>
                <select id="ref" className="lt-field" value={assignedId} onChange={(e) => setAssignedId(e.target.value)} required
                        data-testid="client-new-assigned">
                    <option value="" disabled>Seleziona un referente…</option>
                    {internalUsers.map((u) => (<option key={u.id} value={u.id}>{u.name} — {u.email}</option>))}
                </select>

                <div className="lt-divider" />

                <h2 className="lt-section-title" data-testid="client-new-dati-soc-title">Dati societari</h2>
                <p className="lt-helper" style={{ marginTop: "-4px", marginBottom: "16px" }}>Tutti i campi sono opzionali.</p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <div>
                        <label className="lt-label">Capitale sociale</label>
                        <input className="lt-field" value={capitaleSociale} onChange={(e) => setCapitaleSociale(e.target.value)}
                               data-testid="client-new-capitale-sociale" placeholder="€ 10.000,00 i.v." />
                    </div>
                    <div>
                        <label className="lt-label">Numero REA</label>
                        <input className="lt-field" value={numeroRea} onChange={(e) => setNumeroRea(e.target.value)}
                               data-testid="client-new-numero-rea" placeholder="MI-1234567" />
                    </div>
                </div>

                <label className="lt-label">Amministratore</label>
                <input className="lt-field" value={amministratore} onChange={(e) => setAmministratore(e.target.value)}
                       data-testid="client-new-amministratore" placeholder="Mario Rossi" />

                <label className="lt-label">Sede legale</label>
                <input className="lt-field" value={sedeLegale} onChange={(e) => setSedeLegale(e.target.value)}
                       data-testid="client-new-sede-legale" placeholder="Via dell'Impresa 1, 20121 Milano" />

                <label className="lt-label">Sede operativa</label>
                <input className="lt-field" value={sedeOperativa} onChange={(e) => setSedeOperativa(e.target.value)}
                       data-testid="client-new-sede-operativa" placeholder="Via delle Officine 5, 20125 Milano" />

                <label className="lt-label">Cellulari</label>
                <CellulariEditor
                    values={cellulari.length ? cellulari : [""]}
                    onChange={setCellulari}
                    testid="client-new-cellulari-editor"
                />

                <label className="lt-label" style={{ marginTop: 16 }}>Sito web</label>
                <input className="lt-field" value={sitoWeb} onChange={(e) => setSitoWeb(e.target.value)}
                       data-testid="client-new-sito-web" placeholder="www.acme.it" />

                <div className="lt-divider" />

                <h2 className="lt-section-title">Primo utente cliente</h2>
                <p className="lt-helper" style={{ marginTop: "-4px", marginBottom: "16px" }}>
                    Riceverà un&apos;email con un link per impostare la password (valido 7 giorni).
                </p>

                <label htmlFor="cuname" className="lt-label">Nome</label>
                <input id="cuname" className="lt-field" value={cuName} onChange={(e) => setCuName(e.target.value)} required
                       data-testid="client-new-cu-name" placeholder="Mario Rossi" />

                <label htmlFor="cuemail" className="lt-label">Email</label>
                <input id="cuemail" className="lt-field" type="email" value={cuEmail} onChange={(e) => setCuEmail(e.target.value)} required
                       data-testid="client-new-cu-email" placeholder="mario@acme.it" />

                {error ? (
                    <div data-testid="client-new-error" style={{ fontSize: "13px", color: "#B23A48", margin: "0 0 12px 0" }}>{error}</div>
                ) : null}

                <div className="flex items-center justify-end gap-3" style={{ marginTop: "8px" }}>
                    <Link to="/app/clienti" className="lt-btn-secondary" data-testid="client-new-cancel">Annulla</Link>
                    <button type="submit" className="lt-btn-primary" disabled={loading} onClick={onSubmit} data-testid="client-new-submit">
                        {loading ? "Creazione…" : "Crea società e invia invito"}
                    </button>
                </div>
            </form>
        </div>
    );
}
